import { billing } from '@lawbee/domain';
import { Badge, Button, cn } from '@lawbee/ui';
import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

export type PricingPlanId = billing.PaidPlanId;

/**
 * Both LawBee plans from `billing.PLANS` (CAD per firm per month). `action` renders the button for
 * a plan (sign-up link on the marketing site, checkout on the billing page).
 */
export function PricingCards({
  action,
  currentPlan,
  className,
}: {
  action?: (plan: PricingPlanId) => ReactNode;
  currentPlan?: string | null;
  className?: string;
}) {
  return (
    <div className={cn('grid gap-5 md:grid-cols-2', className)}>
      {billing.PAID_PLANS.map((id) => {
        const p = billing.PLANS[id];
        const featured = !!p.badge;
        return (
          <section
            key={id}
            aria-labelledby={`plan-${id}`}
            className={cn('relative flex flex-col rounded-2xl bg-card p-7 shadow-card', featured && 'ring-2 ring-primary')}
          >
            <div className="flex items-center justify-between gap-3">
              <h3 id={`plan-${id}`} className="text-lg font-semibold">
                {p.name}
              </h3>
              {p.badge ? <Badge variant="primary">{p.badge}</Badge> : null}
              {currentPlan === id ? <Badge variant="success">Current plan</Badge> : null}
            </div>
            <p className="mt-1.5 text-sm text-muted-foreground">{p.tagline}</p>
            <p className="mt-5 flex items-baseline gap-1.5">
              <span className="figures text-[40px] font-semibold leading-none tracking-tight" data-testid={`price-${id}`}>
                {billing.formatPrice(p)}
              </span>
              <span className="text-sm text-muted-foreground">CAD / firm / month</span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {p.limits.users === null ? 'Unlimited team members' : `Up to ${p.limits.users} team members`} ·{' '}
              {p.limits.openMatters === null ? 'unlimited open matters' : `${p.limits.openMatters} open matters`}
            </p>
            <ul className="mt-6 flex-1 space-y-2.5 text-sm">
              {p.highlights.map((h) => (
                <li key={h} className="flex gap-2.5">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            {action ? <div className="mt-7">{action(id)}</div> : null}
          </section>
        );
      })}
    </div>
  );
}

/** Feature comparison rows (Starter vs Growth). */
export function PlanComparison() {
  return (
    <div className="overflow-x-auto rounded-2xl bg-card shadow-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="px-5 py-3 text-left font-medium text-muted-foreground">Feature</th>
            {billing.PAID_PLANS.map((id) => (
              <th key={id} className="px-5 py-3 text-center font-medium">
                {billing.PLANS[id].name.replace('LawBee ', '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {billing.PLAN_FEATURES.map((f) => (
            <tr key={f} className="border-b border-border/60 last:border-0">
              <td className="px-5 py-2.5">{billing.PLAN_FEATURE_LABELS[f]}</td>
              {billing.PAID_PLANS.map((id) => (
                <td key={id} className="px-5 py-2.5 text-center">
                  {billing.PLANS[id].features.includes(f) ? (
                    <Check className="mx-auto size-4 text-success" aria-label="Included" />
                  ) : (
                    <span className="text-muted-foreground" aria-label="Not included">
                      –
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TrialNote() {
  return (
    <p className="text-center text-sm text-muted-foreground">
      Every firm starts with a <span className="font-medium text-foreground">{billing.TRIAL_DAYS}-day free trial</span> of all Growth features. No card needed to start.
    </p>
  );
}

export function SignupButton({ children = 'Start free trial' }: { children?: ReactNode }) {
  return (
    <Button asChild size="lg" className="w-full">
      <Link to="/signup">{children}</Link>
    </Button>
  );
}
